import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, Dropdown } from 'basic-components';
import { DataStorageQuery, Query } from 'dashboard-services';

import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import properties from 'resources/constants/properties.json';

import { useQuery } from '@tanstack/react-query';
import { FocusTrap } from 'focus-trap-react';

import './SymbolColumns.scss';

const useGetColumns = ({ symbol, getQueryBuilder, source }) => 
  useQuery({
    ...DataStorageQuery.QUERIES.getSymbolColumns(getQueryBuilder, {
      groupName: symbol.groupName,
      query: undefined,
      symbols: symbol.symbols,
      metadatas: symbol.metadata,
      size: 50,
      metadata: true,
      useEs: properties.useEsSymbolListing === "true",
      provider: source
    }),
    select: response => (response?.items || []).map(item => item?.name),
  })

const emptyArray = [],
      emptyFun = () => {}
const SymbolColumns = ({ symbolColumns, groupName, onClose, symbols, metadata, value, pattern, index }) => {
  const dispatch = useDispatch(),
        source = useSelector(state => state.globalState.source),
        selectedColumns = useSelector(state => state.seriesState.creatorState.selectedColumns) || emptyArray,
        defaultColumns = useMemo(() => symbolColumns && symbolColumns?.length > 0 ? symbolColumns : selectedColumns, [symbolColumns, selectedColumns]),
        [localColumns, setLocalColumns] = useState(defaultColumns),
        [isMenuOpen, setIsMenuOpen] = useState(false),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
            .withHeader("Target", source, true)
        , [getApiConfigLocal, source]),
        { data: columns = emptyArray } = useGetColumns({ getQueryBuilder, symbol: { groupName, symbols, metadata, value, pattern }, source }),
        onApply = useCallback(() => {
          dispatch(creatorSeriesActions.onChangeSymbolColumns(localColumns, index))
          onClose()
        }, [dispatch, index, localColumns, onClose]),
        setDefaultColumns = useCallback(() => {
          if(symbolColumns) {
            dispatch(creatorSeriesActions.onRemoveSymbolColumns(index))
            onClose()
          }
        }, [dispatch, index, onClose, symbolColumns]),
        timeoutRef = useRef(),
        onMenuOpen = useCallback(() => setIsMenuOpen(true), []),
        onMenuClose = useCallback(() => {
          timeoutRef.current = setTimeout(() => setIsMenuOpen(false), 100)
        }, [])

  useEffect(() => () => {
    clearTimeout(timeoutRef.current)
  }, [])

  return (
    <FocusTrap active>
      <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns__settings">
        <div 
            className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns__settings__bg"
            onClick={isMenuOpen ? emptyFun : onClose}
        />
        <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns__settings__window">
          <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns__settings__window__header">
            Symbol Columns
          </div>
          <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns__settings__window__dropdown">
            <Dropdown
                autoFocus
                defaultValue={defaultColumns}
                menuPlacement="bottom"
                multi
                name="selectedColumns"
                noError
                onChange={setLocalColumns}
                onMenuClose={onMenuClose}
                onMenuOpen={onMenuOpen}
                options={columns}
                placeholder="Columns"
                variant="border"
            />
          </div>
          <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns__settings__window__footer">
            <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns__settings__window__footer__left">
              <Button
                  onClick={setDefaultColumns}
                  size="small"
                  variant="text"
              >
                Default
              </Button>
            </div>
            <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns__settings__window__footer__right">
              <Button
                  onClick={onClose}
                  size="small"
                  variant="text"
              >
                Cancel
              </Button>
              <Button
                  onClick={onApply}
                  size="small"
                  variant="secondary"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </div>
    </FocusTrap>
  )
}

SymbolColumns.defaultProps = {
  groupName: undefined,
  metadata: {},
  pattern: "true",
  symbolColumns: [],
  symbols: {},
  value: undefined,
}

SymbolColumns.propTypes = {
  groupName: PropTypes.string,
  index: PropTypes.number.isRequired,
  metadata: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  pattern: PropTypes.string,
  symbolColumns: PropTypes.array,
  symbols: PropTypes.object,
  value: PropTypes.any,
}

export default SymbolColumns;